/**
 * 배송 관리 = 출고 대장 어휘 (DISPATCH_PLAN.md §3.2 · §3.5).
 * 종류(왜 나가는가)와 배송사. 서버 값과 같은 키를 쓴다(API `DispatchType.ts`).
 */

export type DispatchKind =
	| 'sale'
	| 'wholesale'
	| 'promo'
	| 'repair'
	| 'replacement'
	| 'support'
	| 'outsourcing'
	| 'inbound'

export interface DispatchKindOption {
	value: DispatchKind
	label: string
	/** 방향 — 입고만 in. 칩은 `.faint` */
	direction: 'in' | 'out'
	/** 주문에서 자동으로 정해지는 종류(주문이 있는 건은 못 바꾼다) */
	fromOrder: boolean
	/** 「배송 건 추가」에서 고를 수 있는가. 도매는 주문 없이 거래처에 보낸 것도 적어야 해서 둘 다 */
	manual: boolean
}

export const DISPATCH_KIND_OPTIONS: DispatchKindOption[] = [
	{ value: 'sale', label: '판매', direction: 'out', fromOrder: true, manual: false },
	{ value: 'wholesale', label: '도매', direction: 'out', fromOrder: true, manual: true },
	{
		value: 'promo',
		label: '광고·협찬',
		direction: 'out',
		fromOrder: false,
		manual: true,
	},
	{ value: 'repair', label: '수리', direction: 'out', fromOrder: false, manual: true },
	{
		value: 'replacement',
		label: '교체',
		direction: 'out',
		fromOrder: false,
		manual: true,
	},
	{ value: 'support', label: '지원', direction: 'out', fromOrder: false, manual: true },
	{
		value: 'outsourcing',
		label: '외주',
		direction: 'out',
		fromOrder: false,
		manual: true,
	},
	{ value: 'inbound', label: '입고', direction: 'in', fromOrder: false, manual: true },
]

export const DISPATCH_KIND_TITLE: { [key: string]: string } = Object.fromEntries(
	DISPATCH_KIND_OPTIONS.map(o => [o.value, o.label]),
)

/** 수동 건이 고를 수 있는 종류. 자사몰 판매만 주문에서만 생긴다(2026-10-14 사용자 지시로 도매 포함) */
export const MANUAL_DISPATCH_KINDS: DispatchKindOption[] = DISPATCH_KIND_OPTIONS.filter(
	o => o.manual,
)

export type DispatchCarrier =
	| 'fedex'
	| 'koreapost'
	| 'hanjin'
	| 'gs'
	| 'cj'
	| 'lotte'
	| 'hand'
	| 'other'

export interface DispatchCarrierOption {
	value: DispatchCarrier
	label: string
	/** 송장 조회 주소 — 뒤에 송장번호를 붙인다. 없으면 링크를 걸지 않는다 */
	trackUrl: string
}

/** 엑셀 실측: 한진 47 · 우체국 35 · FedEx 24 · GS 1 (DISPATCH_PLAN §3.5) */
export const DISPATCH_CARRIER_OPTIONS: DispatchCarrierOption[] = [
	{
		value: 'fedex',
		label: 'FedEx',
		trackUrl: 'https://www.fedex.com/fedextrack/?trknbr=',
	},
	{
		value: 'hanjin',
		label: '한진',
		trackUrl:
			'https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mession=&wblnum=',
	},
	{
		value: 'koreapost',
		label: '우체국',
		trackUrl: 'https://service.epost.go.kr/trace.RetrieveDomRigiTraceList.comm?sid1=',
	},
	{
		value: 'cj',
		label: 'CJ대한통운',
		trackUrl: 'https://trace.cjlogistics.com/next/tracking.html?wblNo=',
	},
	{
		value: 'lotte',
		label: '롯데',
		trackUrl: 'https://www.lotteglogis.com/home/reservation/tracking/linkView?InvNo=',
	},
	{
		value: 'gs',
		label: 'GS',
		trackUrl: 'https://www.cvsnet.co.kr/invoice/tracking.do?invoice_no=',
	},
	{ value: 'hand', label: '직접 전달', trackUrl: '' },
	{ value: 'other', label: '기타', trackUrl: '' },
]

export const DISPATCH_CARRIER_TITLE: { [key: string]: string } = Object.fromEntries(
	DISPATCH_CARRIER_OPTIONS.map(o => [o.value, o.label]),
)

/** 송장 조회 링크. 배송사를 모르거나 조회 주소가 없으면 '' */
export const dispatchTrackUrl = (carrier: string, trackingNumber: string): string => {
	if (trackingNumber === '') return ''
	const option = DISPATCH_CARRIER_OPTIONS.find(o => o.value === carrier)
	return option === undefined || option.trackUrl === ''
		? ''
		: `${option.trackUrl}${encodeURIComponent(trackingNumber)}`
}

/**
 * 배송 관리 상태 어휘 — **오더 관리와 같은 단어·같은 사다리**다(2026-09-29 사용자 지시).
 * 서버가 하나만 골라 준다(`Dispatch.ladder`) — 칩도 하나다. 색은 `boStatus`.
 *
 * 입금 대기 · 신규 주문은 주문확인 뒤에 배송 건이 생기므로 실제로는 서지 않는다. 사다리를 그대로
 * 옮겨 두는 것이라 단어는 남긴다.
 */
export const DISPATCH_STATUS_TITLE: { [key: string]: string } = {
	awaitingPayment: '입금 대기',
	new: '신규 주문',
	preparing: '배송 준비중',
	inTransit: '배송중',
	completed: '배송 완료',
	refunded: '환불',
	deleted: '취소',
}

/**
 * 상태 필터. 기본은 **진행중** — 배송 관리는 내보낼 것이 서는 자리라 완료 · 환불 · 취소는 빼 둔다.
 * 끝난 건은 상태를 골라야 보인다(이력은 지우지 않는다).
 */
export const DISPATCH_STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
	{ value: 'active', label: '진행중' },
	{ value: 'preparing', label: '배송 준비중' },
	{ value: 'inTransit', label: '배송중' },
	{ value: 'completed', label: '배송 완료' },
	{ value: 'refunded', label: '환불' },
	{ value: 'deleted', label: '취소' },
	{ value: 'all', label: '전체' },
]
